import React from 'react';
import { GuestGroup } from '../types/ui/Guests';
import { Room } from '../types/Planner';
import '../styles/components/GuestSeatingPanel.css';
import EmptyState from './EmptyState';

interface GuestSeatingPanelProps {
    groups: GuestGroup[];
    rooms: Room[];
    onUnassignGroup: (roomId: string) => void;
}

export const GuestSeatingPanel: React.FC<GuestSeatingPanelProps> = ({
    groups,
    rooms,
    onUnassignGroup,
}) => {
    const tables = rooms.filter(r => r.type === 'table');

    const assignedGroupIds = new Set(tables.map(t => t.groupId).filter(Boolean) as string[]);

    const unassigned = groups.filter(g => !assignedGroupIds.has(g.id));
    const assigned = groups.filter(g => assignedGroupIds.has(g.id));

    const handleDragStart = (e: React.DragEvent, groupId: string) => {
        e.dataTransfer.setData('groupId', groupId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const tablesForGroup = (groupId: string) =>
        tables.filter(t => t.groupId === groupId).map(t => t.name).join(', ');

    return (
        <div className="gsp-panel">
            <div className="gsp-header">
                <span className="gsp-title">Guest Groups</span>
                <span className="gsp-subtitle">{unassigned.length} unseated</span>
            </div>

            {unassigned.length > 0 && (
                <div className="gsp-section">
                    <div className="gsp-section-label">Drag to a table ↓</div>
                    {unassigned.map(group => (
                        <div
                            key={group.id}
                            className="gsp-chip gsp-chip--unseated"
                            draggable
                            onDragStart={e => handleDragStart(e, group.id)}
                        >
                            <span className="gsp-dot" style={{ /* ds-dynamic: group color from data */ background: group.color }} />
                            <span className="gsp-name">{group.name}</span>
                            <span className="gsp-count">{group.guestIds.length}</span>
                        </div>
                    ))}
                </div>
            )}

            {assigned.length > 0 && (
                <div className="gsp-section">
                    <div className="gsp-section-label">Seated</div>
                    {assigned.map(group => {
                        const groupTables = tables.filter(t => t.groupId === group.id);
                        return (
                            <div key={group.id} className="gsp-chip gsp-chip--seated">
                                <span className="gsp-dot" style={{ /* ds-dynamic: group color from data */ background: group.color }} />
                                <div className="gsp-chip-info">
                                    <span className="gsp-name">{group.name}</span>
                                    <span className="gsp-table-label">Table {tablesForGroup(group.id)}</span>
                                </div>
                                <span className="gsp-count">{group.guestIds.length}</span>
                                {groupTables.length > 0 && (
                                    <button
                                        className="gsp-unassign"
                                        title="Remove from table"
                                        onClick={() => groupTables.forEach(t => onUnassignGroup(t.id))}
                                    >×</button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {groups.length === 0 && (
                <EmptyState compact title="No guest groups" description="Add groups in the Guests section." />
            )}
        </div>
    );
};
